import { ClipboardIcon, EnvelopeIcon } from "@heroicons/react/20/solid";
import { useMutation } from "@tanstack/react-query";
import { formatDistanceToNowStrict, parseISO } from "date-fns";
import {

  Clock3,
  FileText,
  Loader2,
  Pause,
  PencilLine,
  Play,
  Plus,
  Search,
  Sparkles,
  Trash2,
  Zap,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SlackIcon from "../../../assets/images/slack.svg?react";
import { Button, buttonVariants } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { queryClient, useAllAutomations, useWorkspaceObjectSchema, useWorkspaceSkills } from "../../../hooks/QueryHooks";
import { cn } from "../../../lib/utils";
import { deleteAutomation, updateAutomation } from "../../../services/automation/automationEndpoints";
import useWorkspaceStore from "../../../store/workspace/workspaceStore";
import { toast } from "sonner";
import { buildEntityOptions } from "../../../shared/filter/utils/entityDisplayUtils";
import SignalChatBuilder, { SignalBuilderDraftConfig } from "./signal-ai-builder/components/SignalChatBuilder";
import SignalFilterPopover from "./signal-overview/components/SignalFilterPopover";

type DestinationMeta = {
  key: string;
  label: string;
  Icon: React.ComponentType<any>;
};

const destinationMap: Record<string, DestinationMeta> = {
  send_to_slack: { key: "send_to_slack", label: "Slack", Icon: SlackIcon },
  send_to_inbox: { key: "send_to_inbox", label: "Inbox", Icon: ClipboardIcon },
  send_to_email: { key: "send_to_email", label: "Email", Icon: EnvelopeIcon },
};

const formatRelativeTime = (value?: string) => {
  if (!value) return "just now";
  try {
    return formatDistanceToNowStrict(parseISO(value), { addSuffix: true });
  } catch {
    return value;
  }
};

const statusTone = (status?: string) => (String(status || "").toLowerCase() === "paused" ? "open" : "active");

const FILTER_OPERATOR_LABELS: Record<string, string> = {
  gt: "greater than",
  gte: "greater than or equal",
  lt: "less than",
  lte: "less than or equal",
  eq: "equals",
  ne: "not equals",
  contains: "contains",
  known: "is known",
  unknown: "is unknown",
  range: "in range",
};

const NO_VALUE_OPERATORS = new Set(["known", "unknown"]);

const SignalsPage = () => {
  const workspace = useWorkspaceStore((state) => state.workspace);
  const navigate = useNavigate();
  const [view, setView] = useState<"list" | "builder">("list");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "paused">("all");
  const [search, setSearch] = useState("");
  const [signalToDelete, setSignalToDelete] = useState<any>(null);
  const [externalPrompt, setExternalPrompt] = useState<string | null>(null);
  const [draftSignalConfig, setDraftSignalConfig] = useState<SignalBuilderDraftConfig | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);

  const { data: workspaceSchema }: any = useWorkspaceObjectSchema({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });

  const { data: workspaceSkills } = useWorkspaceSkills({
    workspaceId: workspace?.id,
  });

  const entityOptions = useMemo(
    () => buildEntityOptions(workspaceSchema),
    [workspaceSchema],
  );

  const signalsQuery: any = useAllAutomations({
    workspaceId: workspace?.id,
    automationType: "signal",
    enabled: !!workspace?.id,
  });

  const updateSignalMutation = useMutation({
    mutationFn: ({
      automationId,
      nextStatus,
    }: {
      automationId: string;
      nextStatus: "active" | "paused";
    }) => updateAutomation({
      workspace_id: workspace?.id,
      automation_id: automationId,
      status: nextStatus,
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["all_automations", workspace?.id, "signal"] });
    },
  });

  const deleteSignalMutation = useMutation({
    mutationFn: ({ automationId }: { automationId: string }) => deleteAutomation({
      workspace_id: workspace?.id,
      automation_id: automationId,
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["all_automations", workspace?.id, "signal"] });
      toast.success("Signal deleted.");
    },
    onError: () => {
      toast.error("Unable to delete signal. Please try again later.");
    },
  });

  const signals = useMemo(() => {
    const source = Array.isArray(signalsQuery?.data) ? signalsQuery.data : [];
    return [...source].sort((left: any, right: any) => {
      const leftDate = Date.parse(left?.updated_at || left?.created_at || "") || 0;
      const rightDate = Date.parse(right?.updated_at || right?.created_at || "") || 0;
      return rightDate - leftDate;
    });
  }, [signalsQuery?.data]);

  const filteredSignals = useMemo(() => {
    return signals
      .filter((signal: any) => {
        if (statusFilter === "all") return true;
        return String(signal?.status || "").toLowerCase() === statusFilter;
      })
      .filter((signal: any) => {
        const term = search.trim().toLowerCase();
        if (!term) return true;
        const haystack = `${signal?.name || ""} ${signal?.description || ""}`.toLowerCase();
        return haystack.includes(term);
      });
  }, [search, signals, statusFilter]);

  // ── Draft helpers ──

  const draftFilterCount = useMemo(() => {
    if (!draftSignalConfig?.filters?.predicates?.length) return 0;
    return draftSignalConfig.filters.predicates.reduce(
      (count: number, group: any) => count + ((group?.predicates?.length as number) || 0),
      0,
    );
  }, [draftSignalConfig]);

  const draftFiltersForPopover = useMemo(() => {
    if (draftSignalConfig?.filters?.predicates?.length) {
      return draftSignalConfig.filters;
    }

    return {
      query_type: "OR",
      predicates: [{
        query_type: "AND",
        predicates: [{
          field: "",
          operator: "",
          value: "",
          type: "string",
          entity_type: "column",
        }],
      }],
    };
  }, [draftSignalConfig]);

  const draftFilterRows = useMemo(() => {
    const filters = draftSignalConfig?.filters;
    if (!filters?.predicates?.length) return [];

    const fieldMap = {
      ...(workspaceSchema?.fields?.obj || {}),
      ...(workspaceSchema?.metrics?.obj || {}),
    };

    return filters.predicates
      .map((group: any, groupIndex: number) => {
        const rows = (group?.predicates || [])
          .filter((predicate: any) => predicate?.field && predicate?.operator)
          .map((predicate: any) => {
            const fieldName = predicate.field;
            const operator = predicate.operator;
            const opLabel = FILTER_OPERATOR_LABELS[operator] || operator;
            const fieldLabel = fieldMap[fieldName]?.display_name || fieldName;
            const value = predicate?.value;
            const formattedValue = value === undefined || value === null || value === ""
              ? ""
              : typeof value === "object"
                ? JSON.stringify(value)
                : String(value);
            if (NO_VALUE_OPERATORS.has(operator)) {
              return `${fieldLabel} ${opLabel}`;
            }

            if (!formattedValue) {
              return `${fieldLabel} ${opLabel}`;
            }

            return `${fieldLabel} ${opLabel} ${formattedValue}`;
          });

        return {
          groupLabel: `Group ${groupIndex + 1}`,
          rows,
        };
      })
      .filter((group: any) => group.rows.length > 0);
  }, [draftSignalConfig, workspaceSchema]);

  const handleDraftFiltersApply = useCallback((newFilters: any) => {
    setDraftSignalConfig((prevDraft) => ({
      ...(prevDraft || {}),
      trigger: prevDraft?.trigger || "usage_based",
      entity_relation: prevDraft?.entity_relation || "accounts",
      filters: newFilters,
    }));
    setExternalPrompt(
      "I updated the filters in the right sidebar. Please use the latest sidebar draft filters and continue."
    );
  }, []);

  const handleDraftFieldChange = useCallback(
    (field: string, value: unknown) => {
      setDraftSignalConfig((prev) => ({
        ...(prev || {}),
        [field]: value,
      }));
    },
    [],
  );

  const handleDraftDestinationToggle = useCallback(
    (destinationType: string) => {
      setDraftSignalConfig((prev) => {
        const current = prev?.automation_actions?.actions || [];
        const exists = current.some((a: any) => a.type === destinationType);
        const nextActions = exists
          ? current.filter((a: any) => a.type !== destinationType)
          : [...current, { type: destinationType, assign_to: "owner", assign_to_key: "owner" }];
        return {
          ...(prev || {}),
          automation_actions: {
            ...(prev?.automation_actions || {}),
            suppression_period: prev?.automation_actions?.suppression_period || "all_time",
            run_schedule: prev?.automation_actions?.run_schedule || "0 9 * * *",
            actions: nextActions,
          },
        };
      });
    },
    [],
  );

  const draftDestinationTypes = useMemo(() => {
    return new Set(
      (draftSignalConfig?.automation_actions?.actions || []).map((a: any) => a.type),
    );
  }, [draftSignalConfig?.automation_actions?.actions]);

  // ── Card actions ──

  const toggleSignalStatus = async (signal: any) => {
    if (!signal?.id) return;
    const current = String(signal?.status || "").toLowerCase();
    const nextStatus = current === "paused" ? "active" : "paused";
    try {
      await updateSignalMutation.mutateAsync({
        automationId: signal.id,
        nextStatus,
      });
      toast.success(`Signal ${nextStatus === "active" ? "resumed" : "paused"}.`);
    } catch {
      toast.error("Unable to update signal status. Please try again later.");
    }
  };

  const confirmDeleteSignal = () => {
    if (!signalToDelete?.id) return;
    deleteSignalMutation.mutate(
      { automationId: signalToDelete.id },
      { onSettled: () => setSignalToDelete(null) },
    );
  };

  const openBuilderWithSignal = (signal: any) => {
    // Extract linked skill info from existing run_skills action
    const runSkillsAction = (signal?.automation_actions?.actions || []).find(
      (a: any) => a.type === "run_skills",
    );
    const existingSkillIds = runSkillsAction?.skill_ids || [];
    // Try to find display names from workspaceSkills
    const existingSkills = existingSkillIds.map((id: string) => {
      const found = (workspaceSkills as any[])?.find((s: any) => s.id === id);
      return { id, name: found?.display_name || found?.skill_name || id };
    });

    setDraftSignalConfig({
      name: signal?.name,
      description: signal?.description,
      trigger: signal?.trigger,
      entity_relation: signal?.entity_relation,
      filters: signal?.filters,
      metrics: signal?.metrics,
      sort: signal?.sort,
      selected_fields: signal?.selected_fields,
      automation_actions: signal?.automation_actions,
      editing_signal_id: signal?.id,
      ...(existingSkillIds.length
        ? { selected_skill_ids: existingSkillIds, selected_skills: existingSkills }
        : {}),
    });
    setExternalPrompt(`Let's update the signal "${signal.name}".`);
    setView("builder");
  };

  // ── List View ──

  if (view === "list") {
    return (
      <div className="flex h-full min-h-0 flex-col bg-background">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-muted p-2">
              <Zap className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-foreground">Signals</h1>
              <p className="text-sm text-muted-foreground">
                Automate alerts and notifications based on account activity
              </p>
            </div>
          </div>
          <Button
            onClick={() => {
              setDraftSignalConfig(null);
              setExternalPrompt("I want to create a new signal from scratch.");
              setView("builder");
            }}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            New Signal
          </Button>
        </div>

        {/* Filter bar */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <Tabs
            value={statusFilter}
            onValueChange={(value) => setStatusFilter(value as "all" | "active" | "paused")}
          >
            <TabsList>
              <TabsTrigger value="all">All Signals</TabsTrigger>
              <TabsTrigger value="active">Active</TabsTrigger>
              <TabsTrigger value="paused">Paused</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search signals..."
              className="pl-9"
            />
          </div>
        </div>

        {/* Signal cards */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {signalsQuery?.isLoading ? (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : filteredSignals.length ? (
            <div className="mx-auto max-w-4xl space-y-4 p-6">
              {filteredSignals.map((signal: any) => {
                const isPaused = String(signal?.status || "").toLowerCase() === "paused";
                const actionTypes = (signal?.automation_actions?.actions || []).map((a: any) => a?.type);
                const destinations = Object.keys(destinationMap)
                  .filter((key) => actionTypes.includes(key))
                  .map((key) => destinationMap[key]);

                return (
                  <div
                    key={signal.id}
                    className="group cursor-pointer rounded-lg border border-border bg-background p-5 transition-all hover:border-foreground/20"
                  >
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div className="rounded-lg bg-muted p-2.5">
                        <Zap className="h-5 w-5 text-muted-foreground" />
                      </div>

                      {/* Content — middle column */}
                      <div className="min-w-0 flex-1">
                        <h3 className="mb-1 font-medium text-foreground transition-colors group-hover:text-primary">
                          {signal?.name || "Untitled signal"}
                        </h3>
                        <p className="mb-3 line-clamp-2 text-sm text-muted-foreground">
                          {signal?.description || "No description"}
                        </p>

                        {/* Footer left: author + status */}
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2">
                            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-muted-foreground text-[10px] font-medium text-white">
                              {(signal?.name || "S").split(" ").map((w: string) => w[0]).join("").toUpperCase().slice(0, 2)}
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {signal?.name || "Signal"}
                            </span>
                          </div>

                          <div className="flex items-center gap-1.5">
                            {isPaused ? (
                              <Pause className="h-3 w-3 text-muted-foreground" />
                            ) : (
                              <div className="h-2 w-2 rounded-full bg-foreground" />
                            )}
                            <span className="text-xs capitalize text-muted-foreground">
                              {isPaused ? "paused" : "active"}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Right column: hover actions on top, meta on bottom */}
                      <div className="flex shrink-0 flex-col items-end justify-between self-stretch">
                        {/* Hover action buttons */}
                        <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            title="Ask ORA"
                            onClick={(e) => {
                              e.stopPropagation();
                              openBuilderWithSignal(signal);
                            }}
                          >
                            <Sparkles className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            title="Edit"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!workspace?.unique_identifier || !signal?.id) return;
                              navigate(`/home/${workspace.unique_identifier}/signals/${signal.id}/edit`);
                            }}
                          >
                            <PencilLine className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            title={isPaused ? "Resume" : "Pause"}
                            disabled={updateSignalMutation.isLoading}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSignalStatus(signal).catch(() => {});
                            }}
                          >
                            {isPaused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            title="Delete"
                            disabled={deleteSignalMutation.isLoading}
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget(signal);
                              setSignalToDelete(signal);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>

                        {/* Destinations + time — bottom right */}
                        <div className="flex items-center gap-3">
                          {destinations.length > 0 && (
                            <div className="flex items-center gap-1">
                              {destinations.map((dest) => {
                                const DestIcon = dest.Icon;
                                return <DestIcon key={dest.key} className="h-4 w-4 opacity-50" />;
                              })}
                            </div>
                          )}
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Clock3 className="h-3 w-3" />
                            <span>{formatRelativeTime(signal?.updated_at || signal?.created_at)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="mb-4 rounded-full bg-muted p-4">
                <Zap className="h-8 w-8 text-muted-foreground" />
              </div>
              <p className="text-lg font-medium text-foreground">No signals found</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Try changing filters or create a new signal.
              </p>
            </div>
          )}
        </div>

        {/* Delete confirmation dialog */}
        <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete signal</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete &ldquo;{deleteTarget?.name || "this signal"}&rdquo;? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={confirmDeleteSignal}
              >
                {deleteSignalMutation.isLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog
          open={!!signalToDelete}
          onOpenChange={(open) => {
            if (!open) setSignalToDelete(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Signal</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete &quot;{signalToDelete?.name || "this signal"}&quot;? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className={buttonVariants({ variant: "default" })}
                onClick={confirmDeleteSignal}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  // ── Builder View ──

  return (
    <div className="h-full min-h-0 bg-background">
      <div className="flex h-full min-h-0">
        {/* Left: Chat */}
        <div className="min-w-0 flex-1">
          <SignalChatBuilder
            showBackButton={true}
            onBack={() => setView("list")}
            title="Signals with ORA"
            subtitle="Create and refine signals through chat."
            activeSignalName={draftSignalConfig?.name || null}
            externalPrompt={externalPrompt}
            onExternalPromptConsumed={() => setExternalPrompt(null)}
            draftSignalConfig={draftSignalConfig}
            onDraftSignalConfigChange={(nextDraft) => setDraftSignalConfig(nextDraft)}
          />
        </div>

        {/* Right: Draft preview sidebar */}
        <aside className="flex h-full w-96 shrink-0 flex-col border-l border-border bg-background">
          {/* Sidebar header */}
          <div className="border-b border-border p-4">
            <div className="flex items-center gap-2">
              <div className="rounded-lg bg-muted p-1.5">
                <Zap className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-foreground">Signal Preview</h2>
                <p className="text-xs text-muted-foreground">Configure your signal draft</p>
              </div>
            </div>
          </div>

          {/* Sidebar content */}
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4">
            {/* Name */}
            <div>
              <label className="mb-1 block text-[11px] font-medium text-muted-foreground">Signal Name</label>
              <Input
                value={draftSignalConfig?.name || ""}
                onChange={(e) => handleDraftFieldChange("name", e.target.value)}
                placeholder="e.g. Accounts at risk"
                className="h-8 text-xs"
              />
            </div>

            {/* Description */}
            <div>
              <label className="mb-1 block text-[11px] font-medium text-muted-foreground">Description</label>
              <Textarea
                value={draftSignalConfig?.description || ""}
                onChange={(e) => handleDraftFieldChange("description", e.target.value)}
                placeholder="What does this signal monitor?"
                className="min-h-[56px] resize-none text-xs"
                rows={2}
              />
            </div>

            {/* Entity & Trigger row */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="mb-1 block text-[11px] font-medium text-muted-foreground">Entity</label>
                <Select
                  value={(draftSignalConfig?.entity_relation as string) || "accounts"}
                  onValueChange={(val) => handleDraftFieldChange("entity_relation", val)}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {entityOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-medium text-muted-foreground">Trigger</label>
                <Select
                  value={(draftSignalConfig?.trigger as string) || "usage_based"}
                  onValueChange={(val) => handleDraftFieldChange("trigger", val)}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="usage_based">Success Metric-based</SelectItem>
                    <SelectItem value="event_based">Event-based</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Destinations */}
            <div>
              <label className="mb-1 block text-[11px] font-medium text-muted-foreground">Destinations</label>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(destinationMap).map(([key, meta]) => {
                  const Icon = meta.Icon;
                  const isActive = draftDestinationTypes.has(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handleDraftDestinationToggle(key)}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-[11px] font-medium transition-colors",
                        isActive
                          ? "border-primary/30 bg-primary/10 text-primary"
                          : "border-border bg-muted/30 text-muted-foreground hover:bg-muted",
                      )}
                    >
                      <Icon className="h-3 w-3" />
                      {meta.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Linked Analysis (Skill) */}
            <div>
              <label className="mb-1 block text-[11px] font-medium text-muted-foreground">Linked Analysis</label>
              <Select
                value={(draftSignalConfig?.selected_skill_ids as string[])?.[0] || "_none"}
                onValueChange={(val) => {
                  if (val === "_none") {
                    setDraftSignalConfig((prev) => ({
                      ...(prev || {}),
                      selected_skill_ids: undefined,
                      selected_skills: undefined,
                    }));
                  } else {
                    const skill = (workspaceSkills as any[])?.find((s: any) => s.id === val);
                    setDraftSignalConfig((prev) => ({
                      ...(prev || {}),
                      selected_skill_ids: [val],
                      selected_skills: [{ id: val, name: skill?.display_name || skill?.skill_name || val }],
                    }));
                  }
                }}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="No skill selected" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">None</SelectItem>
                  {((workspaceSkills as any[]) || [])
                    .filter((s: any) => s.visibility !== "private" && s.visibility !== "protected")
                    .map((skill: any) => (
                      <SelectItem key={skill.id} value={skill.id}>
                        <span className="flex items-center gap-1.5" title={skill.description}>
                          <FileText className="h-3 w-3 text-muted-foreground" />
                          {skill.display_name || skill.skill_name}
                        </span>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {draftSignalConfig?.selected_skills?.[0] ? (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Runs per entity when signal fires
                </p>
              ) : null}
            </div>

            {/* Filters */}
            <div>
              <div className="mb-1 flex items-center justify-between gap-2">
                <label className="text-[11px] font-medium text-muted-foreground">
                  Filters {draftFilterCount > 0 && <span className="text-foreground">({draftFilterCount})</span>}
                </label>
                <SignalFilterPopover
                  filters={draftFiltersForPopover}
                  entityRelation={draftSignalConfig?.entity_relation || "accounts"}
                  onApply={handleDraftFiltersApply}
                  width={620}
                  position="bottom-end"
                  target={(
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 px-2 text-[11px]"
                    >
                      Edit Filters
                    </Button>
                  )}
                />
              </div>
              <div className="rounded-md border border-border bg-muted/30 p-2">
                {draftFilterRows.length ? (
                  <div className="space-y-2">
                    {draftFilterRows.map((group: any) => (
                      <div key={group.groupLabel}>
                        <p className="text-[11px] font-medium text-muted-foreground">{group.groupLabel}</p>
                        <div className="mt-1 space-y-1">
                          {group.rows.map((row: string, rowIndex: number) => (
                            <p key={`${group.groupLabel}-${rowIndex}`} className="text-xs text-foreground">
                              {row}
                            </p>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    No filters yet. ORA auto-generates them, or click Edit Filters.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar footer */}
          <div className="border-t border-border p-4">
            <Button
              size="sm"
              variant="default"
              className="h-8 w-full text-[12px]"
              onClick={() => {
                const isEditing = !!(draftSignalConfig as any)?.editing_signal_id;
                setExternalPrompt(
                  isEditing
                    ? "I updated the signal configuration in the right sidebar. Please use the latest sidebar draft to update this existing signal. Show me the summary so I can confirm the changes."
                    : "I updated the signal configuration in the right sidebar. Please use the latest sidebar draft (name, description, entity, trigger, time window, destinations, and filters) and continue."
                );
              }}
              disabled={!draftSignalConfig?.name && !draftFilterCount}
            >
              <Sparkles className="mr-1.5 h-3.5 w-3.5" />
              Send Draft to ORA
            </Button>
          </div>
        </aside>
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete signal</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{deleteTarget?.name || "this signal"}&rdquo;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDeleteSignal}
            >
              {deleteSignalMutation.isLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SignalsPage;
